// src/routes/GuestRoom.tsx
// Voller Gäste-Flow (audio-only + opt-in Cam). Vorstufen als Customer-Frontend (CustomerPage),
// Live-View in der geteilten 3-Spalten-Live-Shell. Funktionale Verdrahtung unverändert.
import { useEffect, useRef, useState } from 'react';
import { GuestConference, getOrCreateGuestId } from '../lib/conference/conference-session';
import { CamPreview } from '../components/CamPreview';
import { CamTile } from '../components/CamTile';
import { InputDeviceMeter } from '../components/InputDeviceMeter';
import { CustomerPage, CardTitle, Field, TextInput, Select, Pill, Hint, Row } from '../components/live/CustomerPage';
import { LiveShell, ColLabel } from '../components/live/LiveShell';
import { VideoStage } from '../components/live/VideoStage';
import { PermissionNotice } from '../components/live/PermissionNotice';
import { useMediaSetup } from '../hooks/useMediaSetup';

type Room = { name: string; active: boolean; requiresPassword: boolean };
type Phase = 'loading' | 'not-found' | 'password' | 'prejoin' | 'waiting' | 'live' | 'reconnecting' | 'kicked' | 'ended' | 'room-full';

export function GuestRoom({ token }: { token: string }) {
  const [room, setRoom] = useState<Room | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [inputId, setInputId] = useState(() => localStorage.getItem(`tro-guest-mic:${token}`) ?? '');
  const [outputId, setOutputId] = useState(() => localStorage.getItem(`tro-guest-out:${token}`) ?? '');
  const [muted, setMuted] = useState(false);
  const [confGuests, setConfGuests] = useState<{ guestId: string; name: string }[]>([]);
  const [confSpeakers, setConfSpeakers] = useState<{ speakerId: string; name: string }[]>([]);
  const confRef = useRef<GuestConference | null>(null);

  // Geräte + Berechtigung (Mikro + Kamera) am Setup-Screen — siehe useMediaSetup.
  const media = useMediaSetup(phase === 'prejoin');
  const { inputs, outputs, cams } = media;
  const [camId, setCamId] = useState(() => localStorage.getItem(`tro-guest-cam:${token}`) ?? '');   // "" = No Video (aus); sonst Kamera-Device-ID
  const [hostVideoTrack, setHostVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [peerVideos, setPeerVideos] = useState<Map<string, MediaStreamTrack>>(new Map());
  const [selfCamTrack, setSelfCamTrack] = useState<MediaStreamTrack | null>(null);   // eigenes Kamerabild
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`/api/room/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('404'))))
      .then((r) => { const room = r as Room; if (!alive) return; setRoom(room); setPhase(room.requiresPassword ? 'password' : 'prejoin'); })   // Passwort zuerst (falls nötig), dann Geräte-Screen
      .catch(() => { if (alive) setPhase('not-found'); });
    return () => { alive = false; };
  }, [token]);

  // Raum-Status (offen/zu) live nachführen, solange wir im Setup oder Warteraum sind — nur für den Hinweistext.
  useEffect(() => {
    if (phase !== 'prejoin' && phase !== 'waiting') return;
    const id = setInterval(() => {
      fetch(`/api/room/${token}`).then((r) => r.json()).then((r) => setRoom(r as Room)).catch(() => {});
    }, 4000);
    return () => clearInterval(id);
  }, [phase, token]);

  useEffect(() => () => { confRef.current?.teardown(); }, []);

  // Tab-Schliessen/Navigieren → Sofort-Leave per Beacon (sonst hängt der Gast 30s auf "reconnecting").
  useEffect(() => {
    const onHide = () => confRef.current?.byeBeacon();
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // Im aktiven Call vor versehentlichem Schliessen/Reload warnen (wie beim Sprecher).
  useEffect(() => {
    if (phase !== 'live' && phase !== 'reconnecting') return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase]);

  const join = async () => {
    if (joining) return;
    setJoining(true);
    const conf = new GuestConference(token, getOrCreateGuestId(token), name || 'Guest',
      { password: room?.requiresPassword ? password : undefined, inputDeviceId: inputId, outputDeviceId: outputId, cameraDeviceId: camId || undefined },
      {
        onState: (s) => setPhase(s),
        onPeers: (guests, speakers) => {
          setConfGuests(guests.map((g) => ({ guestId: g.guestId, name: g.name })));
          setConfSpeakers(speakers.map((s) => ({ speakerId: s.speakerId, name: s.name })));
        },
        onHostVideo: (track) => setHostVideoTrack(track),
        onPeerVideo: (key, track) => setPeerVideos((prev) => { const next = new Map(prev); if (track) next.set(key, track); else next.delete(key); return next; }),
        onLocalVideo: (track) => setSelfCamTrack(track),
      },
    );
    confRef.current = conf;
    try { await conf.join(); setPhase('waiting'); }
    catch (e) {
      // Wahrscheinlichste Ursache bei Passwort-Raum = falsches Passwort → zurück zum Passwort-Screen.
      if (room?.requiresPassword) { setPwError('Beitritt fehlgeschlagen – bitte Passwort prüfen.'); setPhase('password'); }
      else alert(`Join fehlgeschlagen: ${e instanceof Error ? e.message : e}`);
    } finally { setJoining(false); }
  };
  // Passwort beim "Weiter" tatsächlich prüfen — erst bei korrektem PW geht's zum Device-Setup.
  const submitPassword = async () => {
    if (!password || verifying) return;
    setVerifying(true); setPwError(null);
    try {
      const r = await fetch(`/api/room/${token}/verify`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ password }) });
      const j = (await r.json()) as { ok: boolean };
      if (j.ok) setPhase('prejoin'); else setPwError('Falsches Passwort.');
    } catch { setPwError('Prüfung fehlgeschlagen – bitte erneut versuchen.'); }
    finally { setVerifying(false); }
  };
  const toggleMute = () => { const n = !muted; setMuted(n); confRef.current?.setMuted(n); };
  const leave = () => { confRef.current?.leave(); confRef.current = null; setPhase('ended'); };
  // Warteraum-„BACK": Konferenz verlassen, aber zurück zum Geräte-Screen (nicht zum Ende-Screen) →
  // Gast kann Mikro/Cam ändern und erneut beitreten.
  const backToPrejoin = () => { confRef.current?.leave(); confRef.current = null; setPhase('prejoin'); };

  const guestUrl = typeof window !== 'undefined' ? `${window.location.origin}/${token}` : '';

  // ---- Endzustände + Vorstufen (Customer-Frontend) ----
  if (phase === 'loading') return <CustomerPage studio=""><Hint>Lädt…</Hint></CustomerPage>;
  if (phase === 'not-found') return <CustomerPage studio=""><Hint tone="error">Link ungültig.</Hint></CustomerPage>;
  if (phase === 'room-full') return <CustomerPage studio="" title={room?.name}><Hint>Der Raum ist gerade voll — bitte später erneut versuchen.</Hint></CustomerPage>;
  if (phase === 'kicked' || phase === 'ended') return <CustomerPage studio="" title={room?.name}><CardTitle>Session beendet</CardTitle><Hint>Danke fürs Dabeisein!</Hint></CustomerPage>;

  if (phase === 'password') return (
    <CustomerPage studio="" title={room?.name} token={token}>
      <CardTitle>{room?.name ?? 'Session'}</CardTitle>
      <Hint>Diese Session ist passwortgeschützt.</Hint>
      <Field label="Passwort">
        <TextInput type="password" autoFocus value={password} onChange={(e) => { setPassword(e.target.value); setPwError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') void submitPassword(); }} />
      </Field>
      {pwError && <Hint tone="error">{pwError}</Hint>}
      <Pill primary disabled={!password || verifying} onClick={() => void submitPassword()}>{verifying ? 'Prüfe…' : 'Weiter'}</Pill>
    </CustomerPage>
  );

  if (phase === 'prejoin') return (
    <CustomerPage studio="" title={room?.name} token={token}>
      <CardTitle>Welcome</CardTitle>
      <PermissionNotice ready={media.ready} camReady={media.camReady} requesting={media.requesting} onRequest={() => void media.request()} />
      <Field label="Name"><TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Dein Name" /></Field>
      <Field label="Mikrofon"><Select value={inputId} onChange={(e) => { setInputId(e.target.value); localStorage.setItem(`tro-guest-mic:${token}`, e.target.value); }}>
        <option value="">Standard</option>{inputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>)}
      </Select></Field>
      <Row><span style={{ fontSize: 'var(--text-label)', textTransform: 'uppercase', letterSpacing: 'var(--ls-label)', opacity: 0.6 }}>Signal</span> <InputDeviceMeter deviceId={inputId} /></Row>
      <Field label="Ausgabe"><Select value={outputId} onChange={(e) => { setOutputId(e.target.value); localStorage.setItem(`tro-guest-out:${token}`, e.target.value); }}>
        <option value="">Standard</option>{outputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>)}
      </Select></Field>
      <Field label="Kamera"><Select value={camId} onChange={(e) => { setCamId(e.target.value); localStorage.setItem(`tro-guest-cam:${token}`, e.target.value); }}>
        <option value="">No Video</option>{cams.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>)}
      </Select></Field>
      {camId && <Row><CamPreview deviceId={camId} /></Row>}
      <Hint tone={room?.active ? 'live' : 'muted'}>
        {room?.active
          ? '● TRO hat die Session geöffnet — du kannst beitreten.'
          : 'TRO hat die Session noch nicht geöffnet. Du kannst dich schon einrichten und beitreten — du landest dann im Warteraum.'}
      </Hint>
      <Pill primary onClick={join} disabled={joining}>{joining ? 'Beitreten…' : 'Join'}</Pill>
    </CustomerPage>
  );

  if (phase === 'waiting' || phase === 'reconnecting') return (
    <CustomerPage studio="" title={room?.name} token={token}>
      <CardTitle>Welcome</CardTitle>
      <Hint>{
        phase === 'reconnecting' ? 'Reconnecting…'
        : room?.active ? 'Warte auf Freigabe durch TRO… bitte warten.'
        : 'Warte, bis TRO die Session öffnet…'
      }</Hint>
      <Pill onClick={backToPrejoin}>Back</Pill>
    </CustomerPage>
  );

  // ---- Live-View (geteilte 3-Spalten-Shell) ----
  return (
    <LiveShell
      studio=""
      title={room?.name}
      status="recording"
      token={token}
      left={
        <>
          <ColLabel>Studio Connections</ColLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
            <div style={{ fontWeight: 'var(--weight-medium)' }}>Studio</div>
            {confSpeakers.length > 0
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>{confSpeakers.map((s) => <CamTile key={`sp:${s.speakerId}`} track={peerVideos.get(`sp:${s.speakerId}`) ?? null} label={s.name} />)}</div>
              : <div style={{ fontSize: 'var(--text-p-small)', opacity: 0.5 }}>Kein Sprecher-Video</div>}
          </div>
        </>
      }
      center={
        <>
          <ColLabel>Video</ColLabel>
          <VideoStage track={hostVideoTrack} />
        </>
      }
      right={
        <>
          <ColLabel>Conference</ColLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-xs)' }}>
            {selfCamTrack && <CamTile track={selfCamTrack} label="Du" />}
            {confGuests.map((g) => <CamTile key={`g:${g.guestId}`} track={peerVideos.get(`g:${g.guestId}`) ?? null} label={g.name} />)}
          </div>
          {confGuests.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2xs)' }}>
              {confGuests.map((g) => <span key={g.guestId} style={{ fontSize: 'var(--text-label)', padding: '2px 10px', border: '1px solid var(--color-charcoal-10)', borderRadius: 'var(--radius-pill)' }}>{g.name}</span>)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
            <Pill onClick={toggleMute}>{muted ? 'Unmute' : 'Mute'}</Pill>
          </div>
          {guestUrl && (
            <div style={{ fontSize: 'var(--text-label)', opacity: 0.5, wordBreak: 'break-all' }}>
              <span style={{ textTransform: 'uppercase', letterSpacing: 'var(--ls-label)' }}>Link</span><br />{guestUrl}
            </div>
          )}
        </>
      }
      action={<Pill danger onClick={leave}>Beenden</Pill>}
    />
  );
}
